import { CreatePinScreenComponent } from '@1bill-app/components/create-pin-screen/create-pin-screen.component';
import { RemovePinScreenComponent } from '@1bill-app/components/remove-pin-screen/remove-pin-screen.component';
import { UpdatePinScreenComponent } from '@1bill-app/components/update-pin-screen/update-pin-screen.component';
import { MOBILE_VIEW_THRESHOLD_PX, ROUTE_BASE } from '@1bill-app/constants';
import { PINService } from '@1bill-app/services/auth/pin.service';
import { AuthService } from '@1bill-app/services/auth/state/auth.service';
import { SettingsQuery } from '@1bill-app/services/settings/settings.query';
import { SettingsService } from '@1bill-app/services/settings/settings.service';
import { YodleeService } from '@1bill-app/services/yodlee/yodlee.service';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { AlertController, Platform } from '@ionic/angular';
import { NGXLogger } from 'ngx-logger';
import { HomeService } from 'src/app/services/home/home.service';
import { SubSink } from 'subsink';
import { AuthQuery } from './../../../services/auth/state/auth.query';

interface SettingsData {
  isHeader?: boolean;
  isSecurity?: boolean;
  name: string;
  route?: string;
  icon?: string;
  fullRoute?: boolean;
  type?: 'modal' | 'url' | 'toggle';
}

/**
 * Page for showing settings for app and different other setting options.
 */
@Component({
  selector: 'app-settings',
  templateUrl: './settings.page.html',
  styleUrls: ['./settings.page.scss'],
})
export class SettingsPage implements OnInit {
  settings: SettingsData[];
  subs = new SubSink();
  hasPinEnabled: boolean;
  private hasPinEnabledPriv: boolean;
  get isDesktopView() {
    return this.platform.width() >= MOBILE_VIEW_THRESHOLD_PX;
  }
  constructor(
    private router: Router,
    public alertCtr: AlertController,
    private authService: AuthService,
    private authQuery: AuthQuery,
    private logger: NGXLogger,
    private _settings: SettingsService,
    public query: SettingsQuery,
    private platform: Platform,
    private yodleeService: YodleeService,
    private pinService: PINService,
    private homeService: HomeService,
  ) {
    this.settings = [
      {
        name: 'Contact details',
        route: 'contact-details',
        icon: 'assets/icon/contact-details.svg',
      },
      {
        name: 'Identification (ID) details',
        route: 'id-details',
        icon: 'assets/icon/identification-details.svg',
      },
      // {
      //   name: 'Payment instructions',
      //   route: 'payment-info',
      //   icon: 'assets/icon/payment.svg',
      // },
      {
        name: 'Notifications',
        route: 'notifications',
        icon: 'assets/icon/notification.svg',
      },
      // {
      //   name: 'Link bank accounts',
      //   type: 'modal',
      //   route: 'yodlee-fastlink-modal',
      //   icon: 'assets/icon/bank-accounts.svg',
      // },
      {
        name: 'Dashboard Preferences',
        route: 'dashboard-preferences',
        icon: 'assets/icon/preferences.svg',
      },
      {
        isHeader: true,
        name: 'Security',
      },
      {
        name: 'Change password',
        route: 'password',
        icon: 'assets/icon/login-key.svg',
      },
      {
        name: 'Account Termination',
        route: 'account-delete',
        icon: 'assets/icon/info.svg',
      },
    ];

    if (Capacitor.isNativePlatform()) {
      this.settings = [
        ...this.settings,
        {
          name: 'Require PIN / Biometric Auth',
          icon: 'finger-print-outline',
          type: 'toggle',
          isSecurity: true,
          route: 'PIN',
        },
        {
          name: 'Update PIN',
          icon: 'refresh-outline',
          type: 'modal',
          isSecurity: true,
          route: 'PIN_UPDATE',
        },
      ];
    }
    this.authService.isUserInGroup('DevAdmin').subscribe((isUserInGroup) => {
      this.logger.log('isUserInGroup:', isUserInGroup);
      if (isUserInGroup) {
        this.settings = [
          ...this.settings,
          {
            isHeader: true,
            name: 'Diagnostics',
          },
          {
            name: 'App Logs',
            route: 'diagnostics/app-logs',
            icon: 'assets/icon/info.svg',
            fullRoute: true,
          },
          {
            name: 'Push Logs',
            route: 'diagnostics/push-logs',
            icon: 'assets/icon/notification.svg',
            fullRoute: true,
          },
        ];
        this.logger.log(this.settings);
      }
    });
  }

  async ngAfterViewInit() {
    this.pinService.pinEnabled$.subscribe((res) => {
      this.hasPinEnabled = res;
      this.hasPinEnabledPriv = res;
    });

    // this.market.open('ai.onebill.app');
  }

  linkBankAccounts() {
    this.homeService.presentLinkBankAccountModal();
  }

  async onSettingItemClick(item: SettingsData) {
    if (item.isSecurity) {
      switch (item.route) {
        case 'PIN':
          if (this.hasPinEnabledPriv) {
            this.pinService.openRemovePIN(RemovePinScreenComponent);
          } else {
            await this.pinService.openCreatePIN(CreatePinScreenComponent);
          }
          break;
        case 'PIN_UPDATE':
          if (this.hasPinEnabledPriv) {
            await this.pinService.openUpdatePIN(UpdatePinScreenComponent);
          }
          break;
        default:
          break;
      }
      this.hasPinEnabled = await this.pinService.hasPin();
    } else if (item?.type === 'modal') {
      switch (item.route) {
        case 'yodlee-fastlink-modal':
          this.linkBankAccounts();
          break;
        default:
          break;
      }
    } else this.goToRoute(item.route, item.fullRoute);
  }

  ngOnInit() {
    this.fetchData();
    this.query.select().subscribe({
      next: (settingsData) => {
        // this.logger.log('load settings data: ', settingsData);
      },
      error: (err) => {
        this.logger.error('unable to retrieve user setting from data store', err);
      },
    });
  }

  fetchData() {
    this.subs.sink = this._settings.fetch().subscribe({
      next: (r) => {
        this.logger.log('user settings data fetched:', r);
      },
      error: (e) => {
        this.logger.error('unable to fetch user settings data', e);
      },
    });
    this.subs.sink = this.yodleeService.getAccounts().subscribe();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  goToRoute(routeStr: string, fullRoute?: boolean) {
    this.logger.debug(routeStr);
    if (routeStr === 'app/logout') {
      this.logout();
      return;
    }
    if (fullRoute) {
      this.router.navigate([routeStr]);
    } else {
      this.router.navigate(['app/settings/' + routeStr]).catch((err) => {
        this.logger.log('Route not available yet!');
        throw err;
      });
    }
  }

  // TODO: Remove from settings
  async logout() {
    const alert = await this.alertCtr.create({
      cssClass: 'confirm-logout-alert',
      header: 'Log out',
      // subHeader: 'Log out',
      message: 'Are you sure you want to log out of your account?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          // cssClass: 'secondary',
          handler: (blah) => {
            this.logger.log('Confirm Cancel: blah');
          },
        },
        {
          text: 'Log out',
          cssClass: 'alert-accept',
          handler: (blah) => {
            this.logger.log('Confirm logout: blah');
            this.authService.logout('settings.page').subscribe();
            this.authQuery.isLoggedIn$.subscribe({
              next: (isLoggedIn) => {
                if (!isLoggedIn) this.router.navigate([ROUTE_BASE]);
              },
            });
          },
        },
      ],
    });

    await alert.present();
  }
}
